import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from 'react';
import { DropdownMenu as DM, Popover as P, Tooltip as T, Switch as S } from 'radix-ui';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

const floating =
  'z-[80] rounded-xl border border-border bg-surface text-fg shadow-lg outline-none data-[state=open]:animate-pop-in';

export const DropdownMenu = DM.Root;
export const DropdownMenuTrigger = DM.Trigger;
export const DropdownMenuGroup = DM.Group;

export const DropdownMenuContent = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof DM.Content>>(
  ({ className, sideOffset = 6, align = 'end', ...props }, ref) => (
    <DM.Portal>
      <DM.Content
        ref={ref}
        sideOffset={sideOffset}
        align={align}
        collisionPadding={8}
        className={cn(floating, 'min-w-48 p-1', className)}
        {...props}
      />
    </DM.Portal>
  ),
);
DropdownMenuContent.displayName = 'DropdownMenuContent';

export const DropdownMenuItem = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof DM.Item> & {
    icon?: ReactNode;
    tone?: 'default' | 'danger';
    shortcut?: string;
  }
>(({ className, icon, tone = 'default', shortcut, children, ...props }, ref) => (
  <DM.Item
    ref={ref}
    className={cn(
      'flex h-8 cursor-pointer items-center gap-2 rounded-lg px-2 text-body outline-none select-none',
      'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
      tone === 'danger'
        ? 'text-danger data-[highlighted]:bg-danger-soft'
        : 'text-fg data-[highlighted]:bg-surface-2',
      '[&_svg]:size-4 [&_svg]:shrink-0',
      tone === 'default' && '[&_svg]:text-fg-subtle',
      className,
    )}
    {...props}
  >
    {icon}
    <span className="flex-1 truncate">{children}</span>
    {shortcut && <span className="text-xs text-fg-subtle">{shortcut}</span>}
  </DM.Item>
));
DropdownMenuItem.displayName = 'DropdownMenuItem';

export function DropdownMenuCheckItem({
  checked,
  onSelect,
  children,
}: {
  checked: boolean;
  onSelect: () => void;
  children: ReactNode;
}) {
  return (
    <DM.Item
      onSelect={(e) => {
        e.preventDefault();
        onSelect();
      }}
      className="flex h-8 cursor-pointer items-center gap-2 rounded-lg px-2 text-body text-fg outline-none select-none data-[highlighted]:bg-surface-2"
    >
      <span
        className={cn(
          'flex size-4 items-center justify-center rounded border',
          checked ? 'border-primary bg-primary text-primary-fg' : 'border-border-strong',
        )}
      >
        {checked && <Check className="size-3" strokeWidth={3} />}
      </span>
      <span className="flex-1 truncate">{children}</span>
    </DM.Item>
  );
}

export function DropdownMenuSeparator() {
  return <DM.Separator className="-mx-1 my-1 h-px bg-border" />;
}

export function DropdownMenuLabel({ children }: { children: ReactNode }) {
  return (
    <DM.Label className="px-2 pt-1.5 pb-1 text-caption font-medium tracking-wide text-fg-subtle uppercase">
      {children}
    </DM.Label>
  );
}

export const Popover = P.Root;
export const PopoverTrigger = P.Trigger;
export const PopoverAnchor = P.Anchor;
export const PopoverClose = P.Close;

export const PopoverContent = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof P.Content>>(
  ({ className, sideOffset = 6, align = 'start', ...props }, ref) => (
    <P.Portal>
      <P.Content
        ref={ref}
        sideOffset={sideOffset}
        align={align}
        collisionPadding={8}
        className={cn(floating, 'p-3', className)}
        {...props}
      />
    </P.Portal>
  ),
);
PopoverContent.displayName = 'PopoverContent';

export const TooltipProvider = T.Provider;

export function Tip({
  content,
  children,
  side = 'top',
}: {
  content: ReactNode;
  children: ReactNode;
  side?: 'top' | 'bottom' | 'left' | 'right';
}) {
  if (!content) return <>{children}</>;
  return (
    <T.Root>
      <T.Trigger asChild>{children}</T.Trigger>
      <T.Portal>
        <T.Content
          side={side}
          sideOffset={6}
          collisionPadding={8}
          className="z-[60] max-w-64 rounded-md border border-border-strong bg-tooltip px-2 py-1 text-xs leading-snug text-tooltip-fg shadow-md data-[state=delayed-open]:animate-fade-in"
        >
          {content}
        </T.Content>
      </T.Portal>
    </T.Root>
  );
}

export function Switch({
  id,
  checked,
  onCheckedChange,
  disabled,
  label,
  className,
}: {
  id?: string;
  checked: boolean;
  onCheckedChange: (value: boolean) => void;
  disabled?: boolean;
  label?: string;
  className?: string;
}) {
  return (
    <S.Root
      id={id}
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      aria-label={label}
      className={cn(
        'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border border-transparent transition-colors outline-none',
        'focus-visible:ring-3 focus-visible:ring-primary/30 disabled:cursor-not-allowed disabled:opacity-50',
        checked ? 'bg-primary' : 'bg-surface-3',
        className,
      )}
    >
      <S.Thumb
        className={cn(
          'block size-4 rounded-full bg-switch-thumb shadow-sm ring-0 transition-transform',
          checked ? 'translate-x-[17px]' : 'translate-x-[1px]',
        )}
      />
    </S.Root>
  );
}
